import { IonAlert, IonButton, IonIcon } from '@ionic/react';
import { warningOutline } from 'ionicons/icons';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { once, run } from '../../../helper/electron';
import { cancelWizardDismissed } from '../../../store/slices/ui.slice';

export default function ResetAppData() {
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const dispatch = useDispatch();

  const handleResetApp = () => {
    if (isResetting) return;
    setIsResetting(true);
    console.log('Starting app reset process...');
    // Clear all wizard-related localStorage items
    console.log('Clearing wizard progress and settings...');

    // Clear wizard-related localStorage items
    const wizardItems = [
      'selectedDJSoftware',
      'musicSource',
      'djSoftware',
      'wizardDismissed',
      'hasCompletedSetup', // Clear this so wizard shows again after manual reset
      'startTime',
      'timeSaved',
      'selectedPools',
      'trackMatcherCount',
      'myLibrary_columnConfigs',
    ];

    wizardItems.forEach((item) => {
      localStorage.removeItem(item);
      console.log(`Cleared localStorage item: ${item}`);
    });

    // Reset the wizard dismissed state in Redux
    dispatch(cancelWizardDismissed());
    console.log('Reset wizard dismissed state in Redux');

    // Listen for the response
    once(
      'my-music/reset-database',
      (response: { success: boolean; error?: string }) => {
        console.log('Received reset response from backend:', response);
        if (response?.success) {
          console.log('App reset successful. Restarting application...');

          // Show brief confirmation
          const notification = document.createElement('div');
          notification.textContent = 'Reset complete. Restarting...';
          notification.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: var(--ion-color-success);
            color: white;
            padding: 20px 40px;
            border-radius: 8px;
            z-index: 99999;
            font-size: 16px;
            font-weight: bold;
          `;
          document.body.appendChild(notification);
          // Trigger restart after brief delay
          setTimeout(() => {
            run('restart-app');
          }, 500);
        } else {
          console.error('App reset failed:', response?.error);
          setIsResetting(false);
          alert(
            `Application reset failed: ${response?.error || 'Unknown error'}. Please restart the app manually.`,
          );
        }
      },
    );

    // Trigger the backend reset process
    console.log('Sending reset request to backend...');
    run('my-music/reset-database');
  };

  return (
    <>
      <div className="reset-app-data">
        <div className="reset-info">
          <h3>Reset Application Data</h3>
          <p>
            Permanently delete all library data, settings, and cached
            information. You will be taken back to the setup wizard, but you
            will remain logged in. The application will restart and this action
            cannot be undone.
          </p>
        </div>
        <IonButton
          color="danger"
          fill="outline"
          onClick={() => setShowResetConfirm(true)}
          className="reset-button"
          disabled={isResetting}
        >
          <IonIcon icon={warningOutline} slot="start" />
          {isResetting ? 'Resetting...' : 'Reset App Data'}
        </IonButton>
      </div>

      <IonAlert
        isOpen={showResetConfirm}
        onDidDismiss={() => !isResetting && setShowResetConfirm(false)}
        header={'Confirm Reset'}
        message={`Are you sure you want to delete ALL application data? This includes your library, settings, and wizard progress. You will be taken back to the setup wizard, but you will remain logged in. This action cannot be undone. The app will restart.`}
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
            cssClass: 'secondary',
            handler: () => {
              if (!isResetting) {
                console.log('Reset cancelled');
              }
            },
          },
          {
            text: 'Reset Data',
            role: 'destructive',
            cssClass: 'danger',
            handler: () => {
              console.log('Reset confirmed');
              handleResetApp();
            },
          },
        ]}
      />
    </>
  );
}
